/**
 * `CatalogLevels` — dashboard aziendale "Livelli".
 *
 * Risponde, **per ogni competenza**, a due domande di copertura aziendale:
 *  - quante persone la possiedono a un livello sufficiente, cioè con un
 *    `rankScore >= soglia` (default 50; il match è pesato sull'affidabilità,
 *    ovvero sul numero di skill valutate);
 *  - qual è la **proficiency media** di quelle persone qualificate.
 *
 * I dati provengono da `GET /api/dashboard/catalog-levels` (vista aggregata,
 * aperta a ogni ruolo autenticato) via `useApiClient` + TanStack Query. Le
 * righe arrivano già ordinate per numero di persone qualificate; la tabella
 * resta comunque riordinabile lato client.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../api/useApiClient';
import { scoreColor } from '../components/StarRating';
import {
  CertificationLevelsView,
  CertificationKindFilter,
  type CertificationKindFilterValue,
} from '../components/CertificationLevelsView';
import type {
  CatalogLevelsDto,
  CertificationDto,
  CompetenceCoverageDto,
  CompetenceDto,
  DepartmentDto,
  JobDto,
  Livello,
  SkillDto,
  SkillLevelMatrixDto,
  SkillLevelMatrixRowDto,
  SkillType,
  TeamLevelsDto,
  TeamMemberDto,
} from '../shared/dto';
import {
  LIVELLO_LABELS,
  LIVELLO_VALUES_EVALUABLE,
  SKILL_TYPE_LABELS,
  SKILL_TYPE_VALUES,
} from '../shared/dto';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  Field,
  Icon,
  InfoHint,
  Loading,
  PageHeader,
  TextInput,
  type Column,
} from '../components/ui';

/**
 * Valore del filtro per business unit:
 * - `'all'`: tutte le business unit;
 * - `'none'`: solo le competenze senza reparto assegnato;
 * - `<nome>`: solo le competenze del reparto scelto.
 */
type DepartmentFilter = 'all' | 'none' | string;

/**
 * Filtra le righe per business unit e per query testuale (case-insensitive) sul
 * nome della competenza. Funzione pura, testabile in isolamento.
 */
export function filterCoverage(
  rows: readonly CompetenceCoverageDto[],
  query: string,
  department: DepartmentFilter,
): CompetenceCoverageDto[] {
  // Mostra solo le competenze con almeno una persona qualificata: le righe a
  // zero non aggiungono informazione alla vista di copertura.
  let result = rows.filter((row) => {
    if (row.qualifiedCount === 0) return false;
    if (department === 'all') return true;
    if (department === 'none') return row.departmentName === null;
    return row.departmentName === department;
  });

  const q = query.trim().toLowerCase();
  if (q.length > 0) {
    result = result.filter((row) => row.name.toLowerCase().includes(q));
  }
  return [...result];
}

/** Placeholder per un valore non disponibile (es. `averageProficiency = null`). */
const PLACEHOLDER = '—';

/**
 * Formatta una percentuale `0..100`; `null` → placeholder. I valori oltre il
 * 100% (bonus certificazioni) sono mostrati come "100%+", coerentemente col
 * trattamento oro del resto del software.
 */
function formatMatch(value: number | null): string {
  if (value === null) return PLACEHOLDER;
  return value > 100 ? '100%+' : `${Math.round(value)}%`;
}

/**
 * Barra di percentuale (`0..100`) colorata con la scala rosso→verde dei voti
 * (`scoreColor`, percentuale riportata su `0..10`, come nella card della
 * dashboard). Affianca il valore numerico. `label` personalizza il testo
 * accessibile (es. "Proficiency media" nella vista Competenze, "Match" nella
 * vista Team).
 */
function PercentBar({ value, label }: { value: number; label: string }) {
  // Il valore può superare 100 (bonus certificazioni): la barra si riempie al
  // massimo e assume il trattamento oro con riflesso; l'etichetta mostra "100%+".
  const isOverMax = value > 100;
  const width = Math.min(100, Math.max(0, Math.round(value)));
  const exact = Math.round(Math.max(0, value));
  const color = scoreColor(value / 10) ?? undefined;
  return (
    <span
      className="coverage-bar"
      role="progressbar"
      aria-valuenow={exact}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={isOverMax ? `${label} oltre 100% (${exact}%)` : `${label} ${width}%`}
    >
      <span
        className={[
          'coverage-bar__fill',
          isOverMax ? 'coverage-bar__fill--gold' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{
          width: `${width}%`,
          ...(!isOverMax && color ? { background: color } : {}),
        }}
      />
    </span>
  );
}

/** Colonne della tabella di copertura per competenza. */
function buildColumns(): Column<CompetenceCoverageDto>[] {
  return [
    {
      key: 'name',
      header: 'Competenza',
      sortable: true,
      sortAccessor: (row) => row.name.toLowerCase(),
      render: (row) => (
        <div className="coverage-name">
          <Link
            to={`/insights/competences/${encodeURIComponent(row.competenceId)}`}
            className="coverage-name__link"
            aria-label={`Apri il dettaglio della competenza ${row.name}`}
          >
            {row.name}
            <Icon name="external" size={14} className="coverage-name__link-icon" />
          </Link>
          {row.departmentName !== null && (
            <Badge tone="neutral">{row.departmentName}</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'qualified',
      header: 'Persone qualificate',
      sortable: true,
      sortAccessor: (row) => row.qualifiedCount,
      className: 'coverage-qualified-col',
      render: (row) => (
        <span className="coverage-cell__count">
          {row.qualifiedCount}
          <span className="coverage-cell__total"> / {row.totalPeople}</span>
        </span>
      ),
    },
    {
      key: 'averageProficiency',
      header: 'Proficiency media (qualificate)',
      sortable: true,
      // Le competenze senza persone qualificate (proficiency `null`) in coda.
      sortComparator: (a, b, direction) => {
        const av = a.averageProficiency;
        const bv = b.averageProficiency;
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        const sign = direction === 'desc' ? -1 : 1;
        return sign * (av - bv);
      },
      render: (row) =>
        row.averageProficiency === null ? (
          <span className="coverage-cell__count">{PLACEHOLDER}</span>
        ) : (
          <div className="coverage-cell">
            <PercentBar value={row.averageProficiency} label="Proficiency media" />
            <span
              className={[
                'coverage-cell__count',
                row.averageProficiency > 100 ? 'coverage-cell__count--gold' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {formatMatch(row.averageProficiency)}
            </span>
          </div>
        ),
    },
  ];
}

/** Vista attiva della dashboard "Livelli": Team, competenza, skill o certificazioni. */
type LevelsView = 'team' | 'competences' | 'skills' | 'certifications';

/** Viste ammesse nella query string `?view=`. */
const LEVELS_VIEWS: readonly LevelsView[] = [
  'team',
  'competences',
  'skills',
  'certifications',
];

/** Interpreta il parametro `view` dell'URL, con fallback a `'team'`. */
function parseLevelsView(value: string | null): LevelsView {
  return LEVELS_VIEWS.includes(value as LevelsView) ? (value as LevelsView) : 'team';
}

/**
 * Valore del filtro per competenza nella vista Team (secondo select):
 * - `'all'`: tutte le competenze;
 * - `<id>`: match delle persone sulla competenza scelta.
 */
export type SkillCompetenceFilter = 'all' | string;

/**
 * Prefissi dei valori del filtro di ambito (vista Skill) che puntano a un
 * reparto o a una professione. Le voci generali non hanno prefisso.
 */
const SKILL_DEPT_PREFIX = 'dept:';
const SKILL_JOB_PREFIX = 'job:';

/**
 * Valore del filtro di ambito della vista Skill (allineato ai filtri della
 * pagina di autovalutazione skill):
 * - `'all'`: tutte le skill;
 * - `dept:<id>`: le skill delle competenze del reparto (business unit);
 * - `job:<id>`: le skill delle competenze richieste dalla professione.
 *
 * Skill non hanno un legame diretto con reparti/professioni: l'insieme si deriva
 * risalendo le competenze.
 */
type SkillScopeFilter = 'all' | string;

/**
 * Valore del filtro per tipologia (vista Skill):
 * - `'all'`: qualsiasi tipologia;
 * - `SkillType`: solo le skill di quella tipologia;
 * - `'none'`: solo le skill prive di tipologia (documenti legacy).
 */
type SkillTypeFilter = 'all' | SkillType | 'none';

/**
 * Singola cella della matrice skill × Livello: heatmap colorata secondo il voto
 * medio (`0..10`, scala `scoreColor` rosso→verde) delle persone che hanno
 * dichiarato quel Livello sulla skill, con un badge che riporta il numero di
 * persone e il voto medio.
 *
 * Celle vuote (`count = 0`): riquadro neutro, senza badge. Celle con persone ma
 * senza alcun voto (`averageScore = null`): riquadro neutro con badge che mostra
 * il conteggio e il placeholder "—" al posto del voto.
 */
function MatrixCell({
  cell,
  levelLabel,
}: {
  cell: SkillLevelMatrixRowDto['cells'][Livello];
  levelLabel: string;
}) {
  if (cell.count === 0) {
    return (
      <td className="level-matrix__cell level-matrix__cell--empty" aria-label={`${levelLabel}: nessuna persona`}>
        <span className="level-matrix__empty" aria-hidden="true">
          {PLACEHOLDER}
        </span>
      </td>
    );
  }

  // Voto medio oltre il massimo della scala (bonus certificazioni): trattamento
  // oro con etichetta "10+" e riflesso lucido, coerente con lo `ScoreBadge`
  // della lista skill, a prescindere dal colore della scala rosso→verde.
  const isOverMax = cell.averageScore !== null && cell.averageScore > 10;
  const color = cell.averageScore === null ? null : scoreColor(cell.averageScore);
  const scoreText =
    cell.averageScore === null
      ? PLACEHOLDER
      : isOverMax
        ? '10+'
        : cell.averageScore.toFixed(1);
  const label =
    cell.averageScore === null
      ? `${levelLabel}: ${cell.count} persone, voto non disponibile`
      : isOverMax
        ? `${levelLabel}: ${cell.count} persone, voto medio oltre 10 (${cell.averageScore.toFixed(1)})`
        : `${levelLabel}: ${cell.count} persone, voto medio ${scoreText} su 10`;

  return (
    <td
      className={[
        'level-matrix__cell',
        isOverMax ? 'level-matrix__cell--gold' : '',
        color === null && !isOverMax ? 'level-matrix__cell--muted' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={
        isOverMax || color === null
          ? undefined
          : ({ background: color, '--cell-color': color } as CSSProperties)
      }
      aria-label={label}
    >
      <span className="level-matrix__badge">
        <span className="level-matrix__score">{scoreText}</span>
        <span className="level-matrix__count">{cell.count}</span>
      </span>
    </td>
  );
}

/**
 * Matrice heatmap skill × Livello: una riga per skill, cinque colonne (i Livelli
 * `e-1..e-5`). Ogni cella è colorata secondo il voto medio delle persone che
 * hanno dichiarato quel Livello sulla skill e riporta un badge con numero di
 * persone e voto medio (vedi {@link MatrixCell}).
 *
 * Gestisce da sé gli stati di caricamento/errore/vuoto, coerentemente con la
 * `DataTable` usata dalle altre viste della pagina.
 */
function SkillLevelMatrix({
  rows,
  loading,
  error,
  descriptionBySkillId,
  certCountBySkillId,
}: {
  rows: readonly SkillLevelMatrixRowDto[];
  loading: boolean;
  error: string | null;
  /** Descrizione per skill id (assente ⇒ nessun hint). */
  descriptionBySkillId?: Map<string, string | undefined>;
  /** Numero di certificazioni collegate per skill id (assente ⇒ 0). */
  certCountBySkillId?: Map<string, number>;
}) {
  if (loading) {
    return <Loading>Caricamento della matrice…</Loading>;
  }
  if (error !== null) {
    return <Alert tone="error">{error}</Alert>;
  }
  if (rows.length === 0) {
    return (
      <p className="level-matrix__empty-message">
        Nessuna skill corrisponde alla competenza o alla ricerca.
      </p>
    );
  }

  return (
    <div className="level-matrix" role="region" aria-label="Matrice skill per livello">
      <table className="level-matrix__table">
        <thead>
          <tr>
            <th scope="col" className="level-matrix__skill-head">
              Skill
            </th>
            {LIVELLO_VALUES_EVALUABLE.map((livello) => (
              <th key={livello} scope="col" className="level-matrix__level-head">
                <span className="level-matrix__level-label">{LIVELLO_LABELS[livello]}</span>
              </th>
            ))}
            <th scope="col" className="level-matrix__cert-head">
              Certificazioni
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            // Totale delle figure sulla riga: somma dei conteggi dei Livelli
            // significativi `e-1..e-5` (esclude "Nessuno" `e-0`, non mostrato).
            const rowTotal = LIVELLO_VALUES_EVALUABLE.reduce(
              (sum, livello) => sum + row.cells[livello].count,
              0,
            );
            return (
            <tr key={row.skillId}>
              <th scope="row" className="level-matrix__skill">
                <span className="level-matrix__skill-inner">
                  <Link
                    to={`/insights/skills/${encodeURIComponent(row.skillId)}`}
                    className="coverage-name__link level-matrix__skill-name"
                    aria-label={`Apri il dettaglio della skill ${row.name}`}
                  >
                    {row.name}
                    <Icon
                      name="external"
                      size={14}
                      className="coverage-name__link-icon"
                    />
                  </Link>
                  <InfoHint
                    text={descriptionBySkillId?.get(row.skillId)}
                    label={`Descrizione di ${row.name}`}
                  />
                  {rowTotal > 0 && (
                    <span
                      className="level-matrix__skill-total"
                      aria-label={`${rowTotal} persone in totale su questa skill`}
                    >
                      {rowTotal}
                    </span>
                  )}
                </span>
              </th>
              {LIVELLO_VALUES_EVALUABLE.map((livello) => (
                <MatrixCell
                  key={livello}
                  cell={row.cells[livello]}
                  levelLabel={`${livello.toUpperCase()} ${LIVELLO_LABELS[livello]}`}
                />
              ))}
              {(() => {
                const certCount = certCountBySkillId?.get(row.skillId) ?? 0;
                return (
                  <td
                    className="level-matrix__cert-cell"
                    aria-label={
                      certCount === 0
                        ? `Nessuna certificazione collegata a ${row.name}`
                        : `${certCount} certificazioni collegate a ${row.name}`
                    }
                  >
                    {certCount > 0 && (
                      <span className="level-matrix__cert-count">
                        <Icon name="award" size={14} />
                        {certCount}
                      </span>
                    )}
                  </td>
                );
              })()}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Filtra le righe Team per reparto e per query testuale (case-insensitive) su
 * nome persona, email e nome della competenza principale. Funzione pura.
 *
 * - `department === 'all'`: nessun filtro sul reparto;
 * - `department === 'none'`: solo le persone senza reparto assegnato;
 * - `department === <nome>`: solo le persone del reparto scelto.
 *
 * Il filtro testuale è applicato dopo quello per reparto.
 */
export function filterTeam(
  rows: readonly TeamMemberDto[],
  query: string,
  department: DepartmentFilter = 'all',
): TeamMemberDto[] {
  let result = rows.filter((row) => {
    if (department === 'all') return true;
    if (department === 'none') return row.departmentName === null;
    return row.departmentName === department;
  });

  const q = query.trim().toLowerCase();
  if (q.length > 0) {
    result = result.filter(
      (row) =>
        row.displayName.toLowerCase().includes(q) ||
        row.email.toLowerCase().includes(q) ||
        (row.topCompetenceName !== null &&
          row.topCompetenceName.toLowerCase().includes(q)),
    );
  }
  return [...result];
}

/**
 * Colonne della vista "Team": persona (nome + reparto), competenza principale
 * (rank più alto) e match relativo. Le persone senza match mostrano il
 * placeholder "—".
 *
 * Quando è selezionata una competenza specifica (`showCompetence = false`) la
 * colonna "Competenza principale" è omessa: il match è tutto riferito a quella
 * competenza, quindi ripeterne il nome su ogni riga sarebbe ridondante.
 */
function buildTeamColumns(showCompetence = true): Column<TeamMemberDto>[] {
  const personColumn: Column<TeamMemberDto> = {
    key: 'person',
    header: 'Persona',
    sortable: true,
    sortAccessor: (row) => row.displayName.toLowerCase(),
    // Il nome apre la dashboard della persona (drill-down del Team).
    render: (row) => (
      <div className="coverage-name">
        <Link
          to={`/insights/team/${encodeURIComponent(row.userId)}`}
          className="coverage-name__link"
          aria-label={`Apri la dashboard di ${row.displayName}`}
        >
          {row.displayName}
          <Icon name="external" size={14} className="coverage-name__link-icon" />
        </Link>
        {row.departmentName !== null && (
          <Badge tone="neutral">{row.departmentName}</Badge>
        )}
      </div>
    ),
  };

  const competenceColumn: Column<TeamMemberDto> = {
    key: 'topCompetence',
    header: 'Competenza principale',
    sortable: true,
    sortAccessor: (row) => (row.topCompetenceName ?? '').toLowerCase(),
    render: (row) =>
      row.topCompetenceName === null ? (
        <span className="coverage-cell__count">{PLACEHOLDER}</span>
      ) : (
        <span className="coverage-name__label">{row.topCompetenceName}</span>
      ),
  };

  const proficiencyColumn: Column<TeamMemberDto> = {
      key: 'topProficiency',
      header: 'Proficiency',
      sortable: true,
      // Le persone senza dato (`null`) restano sempre in coda.
      sortComparator: (a, b, direction) => {
        const av = a.topProficiencyPercent;
        const bv = b.topProficiencyPercent;
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        const sign = direction === 'desc' ? -1 : 1;
        return sign * (av - bv);
      },
      render: (row) =>
        row.topProficiencyPercent === null ? (
          <span className="coverage-cell__count">{PLACEHOLDER}</span>
        ) : (
          <div className="coverage-cell">
            <PercentBar value={row.topProficiencyPercent} label="Proficiency" />
            <span
              className={[
                'coverage-cell__count',
                row.topProficiencyPercent > 100 ? 'coverage-cell__count--gold' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {formatMatch(row.topProficiencyPercent)}
            </span>
          </div>
        ),
  };

  return showCompetence
    ? [personColumn, competenceColumn, proficiencyColumn]
    : [personColumn, proficiencyColumn];
}

export default function CatalogLevels() {
  const api = useApiClient();

  // Vista attiva: Team (default), per competenza, per skill o certificazioni.
  // La vista è determinata dalla query string `?view=`, impostata dalla
  // navigazione superiore (sezione Insights nella navbar) e dai back-link dei
  // dettagli (es. `/insights?view=certifications`). La pagina si limita a
  // leggerla dall'URL: non esiste più un tablist interno che la modifica.
  const [searchParams] = useSearchParams();
  const view = parseLevelsView(searchParams.get('view'));

  const { data, isLoading, isError, error, refetch } = useQuery<CatalogLevelsDto>({
    queryKey: ['dashboard', 'catalog-levels'],
    queryFn: () => api.get<CatalogLevelsDto>('/api/dashboard/catalog-levels'),
    enabled: view === 'competences',
  });

  // Filtri della vista Team: reparto e competenza. La competenza (secondo
  // filtro) è opzionale e cambia la semantica della vista: con `'all'` si mostra
  // la competenza principale di ciascuna persona, altrimenti il match sulla
  // competenza scelta.
  const [teamDepartment, setTeamDepartment] = useState<DepartmentFilter>('all');
  const [teamCompetence, setTeamCompetence] = useState<SkillCompetenceFilter>('all');

  // Vista "Team": elenco persone con la loro competenza principale, oppure — se
  // è selezionata una competenza — il loro match su quella competenza. Caricata
  // solo quando la vista è attiva; la `queryKey` include la competenza scelta.
  const teamLevelsQuery = useQuery<TeamLevelsDto>({
    queryKey: ['dashboard', 'team-levels', teamCompetence],
    queryFn: () =>
      api.get<TeamLevelsDto>(
        teamCompetence === 'all'
          ? '/api/dashboard/team-levels'
          : `/api/dashboard/team-levels?competenceId=${encodeURIComponent(teamCompetence)}`,
      ),
    enabled: view === 'team',
  });

  // Matrice skill × Livello (vista "Skill"): caricata solo quando la vista è
  // attiva. Per ogni skill la distribuzione delle persone per Livello dichiarato
  // e il voto medio di ciascuna fascia (heatmap).
  const skillMatrixQuery = useQuery<SkillLevelMatrixDto>({
    queryKey: ['dashboard', 'skill-level-matrix'],
    queryFn: () => api.get<SkillLevelMatrixDto>('/api/dashboard/skill-level-matrix'),
    enabled: view === 'skills',
  });

  // Catalogo skill: serve solo alla vista "Skill" per recuperare la descrizione
  // (assente in `SkillLevelMatrixRowDto`) da mostrare nell'hint accanto al nome.
  const skillsQuery = useQuery<SkillDto[]>({
    queryKey: ['skills'],
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
    enabled: view === 'skills',
  });
  const descriptionBySkillId = useMemo(() => {
    const map = new Map<string, string | undefined>();
    for (const s of skillsQuery.data ?? []) map.set(s.id, s.description);
    return map;
  }, [skillsQuery.data]);

  // Catalogo certificazioni: serve alla vista "Skill" per contare quante
  // certificazioni sono collegate a ciascuna skill (colonna "Certificazioni").
  const certificationsQuery = useQuery<CertificationDto[]>({
    queryKey: ['certifications'],
    queryFn: () => api.get<CertificationDto[]>('/api/certifications'),
    enabled: view === 'skills',
  });
  const certCountBySkillId = useMemo(() => {
    const map = new Map<string, number>();
    for (const cert of certificationsQuery.data ?? []) {
      for (const skillId of cert.skillIds) {
        map.set(skillId, (map.get(skillId) ?? 0) + 1);
      }
    }
    return map;
  }, [certificationsQuery.data]);

  // Catalogo competenze: popola il filtro per competenza delle viste Skill e
  // Team e fornisce la mappa competenza→skill (relazione N:N). Caricato con le
  // due viste che lo usano.
  const competencesQuery = useQuery<CompetenceDto[]>({
    queryKey: ['competences'],
    queryFn: () => api.get<CompetenceDto[]>('/api/competences'),
    enabled: view === 'skills' || view === 'team',
  });

  // Professioni: forniscono le competenze richieste per l'optgroup "Per
  // professione" del filtro di ambito della vista Skill. Caricate solo lì.
  const jobsQuery = useQuery<JobDto[]>({
    queryKey: ['jobs'],
    queryFn: () => api.get<JobDto[]>('/api/jobs'),
    enabled: view === 'skills',
  });

  // Tipologia per skill id (dal catalogo skill), per il filtro per tipologia.
  const skillTypeById = useMemo(() => {
    const map = new Map<string, SkillType | undefined>();
    for (const s of skillsQuery.data ?? []) map.set(s.id, s.type);
    return map;
  }, [skillsQuery.data]);

  // Reparti: mappa id→nome per filtrare le competenze del reparto scelto nel
  // secondo filtro della vista Team, e raggruppare per reparto le competenze
  // del filtro della vista Skill (optgroup). Caricati con entrambe le viste.
  const departmentsQuery = useQuery<DepartmentDto[]>({
    queryKey: ['departments'],
    queryFn: () => api.get<DepartmentDto[]>('/api/departments'),
    enabled: view === 'team' || view === 'skills',
  });

  // Ricerca testuale sul nome e filtri: business unit (vista Competenze) e
  // competenza (vista Skill).
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState<DepartmentFilter>('all');
  // Vista Skill: filtro di ambito (tutte / per business unit / per professione)
  // e filtro per tipologia, allineati alla pagina di autovalutazione skill.
  const [skillScope, setSkillScope] = useState<SkillScopeFilter>('all');
  const [skillType, setSkillType] = useState<SkillTypeFilter>('all');
  // Vista Certificazioni: filtro per tipo (aziendali/personali), mostrato come
  // grouped button accanto al titolo della pagina.
  const [certificationKind, setCertificationKind] =
    useState<CertificationKindFilterValue>('company');

  const columns = useMemo(() => buildColumns(), []);
  // La colonna "Competenza principale" ha senso solo senza competenza scelta.
  const teamColumns = useMemo(
    () => buildTeamColumns(teamCompetence === 'all'),
    [teamCompetence],
  );

  const competences = useMemo(() => data?.competences ?? [], [data]);

  // Business unit disponibili: i nomi reparto distinti presenti nei dati,
  // ordinati; l'opzione "Senza reparto" compare solo se esiste almeno una
  // competenza senza reparto.
  const departmentOptions = useMemo(() => {
    const names = new Set<string>();
    let hasNone = false;
    for (const row of competences) {
      if (row.departmentName === null) hasNone = true;
      else names.add(row.departmentName);
    }
    return {
      names: [...names].sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' })),
      hasNone,
    };
  }, [competences]);

  const filteredRows = useMemo(
    () => filterCoverage(competences, search, department),
    [competences, search, department],
  );

  const skillRows = useMemo(
    () => skillMatrixQuery.data?.skills ?? [],
    [skillMatrixQuery.data],
  );

  // Mappa reparto id→nome. Usata dal filtro competenza della vista Team e dai
  // filtri "per business unit"/"per professione" della vista Skill.
  const departmentNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of departmentsQuery.data ?? []) map.set(d.id, d.name);
    return map;
  }, [departmentsQuery.data]);

  // Mappa competenceId → insieme delle skill collegate (relazione N:N).
  const skillIdsByCompetence = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const c of competencesQuery.data ?? []) {
      map.set(c.id, new Set(c.skillIds));
    }
    return map;
  }, [competencesQuery.data]);

  // reparto → insieme delle skill (unione delle skill delle sue competenze), per
  // il filtro "Per business unit" della vista Skill.
  const skillIdsByDepartment = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const c of competencesQuery.data ?? []) {
      if (c.departmentId === null) continue;
      const set = map.get(c.departmentId) ?? new Set<string>();
      for (const skillId of c.skillIds) set.add(skillId);
      map.set(c.departmentId, set);
    }
    return map;
  }, [competencesQuery.data]);

  // professione → insieme delle skill (unione delle skill delle competenze
  // richieste dal Job), per il filtro "Per professione" della vista Skill.
  const skillIdsByJob = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const job of jobsQuery.data ?? []) {
      const set = new Set<string>();
      for (const competenceId of job.competenceIds) {
        const skillsOfComp = skillIdsByCompetence.get(competenceId);
        if (skillsOfComp) for (const skillId of skillsOfComp) set.add(skillId);
      }
      map.set(job.id, set);
    }
    return map;
  }, [jobsQuery.data, skillIdsByCompetence]);

  // Reparti con almeno una competenza collegata a skill (una voce per reparto),
  // ordinati alfabeticamente, per l'optgroup "Per business unit".
  const skillDepartmentOptions = useMemo(() => {
    const withSkills = new Set<string>();
    for (const c of competencesQuery.data ?? []) {
      if (c.departmentId !== null && c.skillIds.length > 0) {
        withSkills.add(c.departmentId);
      }
    }
    const collator = new Intl.Collator('it', { sensitivity: 'base' });
    return [...withSkills]
      .filter((id) => departmentNameById.has(id))
      .map((id) => ({ id, name: departmentNameById.get(id) as string }))
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [competencesQuery.data, departmentNameById]);

  // Professioni con almeno una competenza (una voce per professione), ordinate
  // alfabeticamente, per l'optgroup "Per professione".
  const skillJobOptions = useMemo(() => {
    const collator = new Intl.Collator('it', { sensitivity: 'base' });
    return (jobsQuery.data ?? [])
      .filter((j) => j.competenceIds.length > 0)
      .map((j) => ({ id: j.id, name: j.name }))
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [jobsQuery.data]);

  // Righe skill filtrate: prima l'ambito, poi la tipologia, infine la ricerca.
  // Le righe della matrice completamente vuote (nessuna persona) sono comunque
  // escluse, come faceva `filterSkillMatrix`.
  const filteredSkillRows = useMemo(() => {
    let result: readonly SkillLevelMatrixRowDto[] = skillRows.filter((row) =>
      LIVELLO_VALUES_EVALUABLE.some((livello) => row.cells[livello].count > 0),
    );

    // 1) Ambito.
    if (skillScope.startsWith(SKILL_DEPT_PREFIX)) {
      const allowed = skillIdsByDepartment.get(
        skillScope.slice(SKILL_DEPT_PREFIX.length),
      );
      result = allowed ? result.filter((r) => allowed.has(r.skillId)) : [];
    } else if (skillScope.startsWith(SKILL_JOB_PREFIX)) {
      const allowed = skillIdsByJob.get(skillScope.slice(SKILL_JOB_PREFIX.length));
      result = allowed ? result.filter((r) => allowed.has(r.skillId)) : [];
    }
    // `'all'`: nessun filtro di ambito.

    // 2) Tipologia.
    if (skillType !== 'all') {
      result = result.filter((r) => {
        const type = skillTypeById.get(r.skillId);
        return skillType === 'none' ? type === undefined : type === skillType;
      });
    }

    // 3) Ricerca testuale sul nome.
    const q = search.trim().toLowerCase();
    if (q.length > 0) {
      result = result.filter((r) => r.name.toLowerCase().includes(q));
    }

    return [...result];
  }, [
    skillRows,
    skillScope,
    skillType,
    search,
    skillIdsByDepartment,
    skillIdsByJob,
    skillTypeById,
  ]);

  const teamRows = useMemo(
    () => teamLevelsQuery.data?.members ?? [],
    [teamLevelsQuery.data],
  );

  // Reparti disponibili nella vista Team: i nomi distinti presenti tra le
  // persone, ordinati; "Senza reparto" compare solo se esiste almeno una
  // persona senza reparto.
  const teamDepartmentOptions = useMemo(() => {
    const names = new Set<string>();
    let hasNone = false;
    for (const row of teamRows) {
      if (row.departmentName === null) hasNone = true;
      else names.add(row.departmentName);
    }
    return {
      names: [...names].sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' })),
      hasNone,
    };
  }, [teamRows]);

  // Competenze del secondo filtro della vista Team, a loro volta filtrate dal
  // reparto selezionato (le competenze senza reparto restano solo con
  // "Tutti i reparti" o "Senza reparto"), ordinate alfabeticamente.
  const teamFilterCompetences = useMemo(() => {
    const all = competencesQuery.data ?? [];
    const filtered = all.filter((c) => {
      if (teamDepartment === 'all') return true;
      if (teamDepartment === 'none') return c.departmentId === null;
      const name = c.departmentId === null ? null : departmentNameById.get(c.departmentId) ?? null;
      return name === teamDepartment;
    });
    return [...filtered].sort((a, b) =>
      a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }),
    );
  }, [competencesQuery.data, teamDepartment, departmentNameById]);

  // Raggruppamento per reparto del secondo select, usato SOLO quando sono
  // selezionati "Tutti i reparti": in tal caso le competenze compaiono in
  // optgroup (reparti in ordine alfabetico, competenze in ordine alfabetico,
  // "Senza reparto" in coda). Con un reparto specifico il select resta piatto
  // (le competenze sono già ristrette a quel reparto) e questo vale `null`.
  const teamCompetenceGroups = useMemo(() => {
    if (teamDepartment !== 'all') return null;
    const ORPHAN = '__none__';
    const byDepartment = new Map<string, CompetenceDto[]>();
    for (const c of teamFilterCompetences) {
      const key =
        c.departmentId !== null && departmentNameById.has(c.departmentId)
          ? c.departmentId
          : ORPHAN;
      const list = byDepartment.get(key) ?? [];
      list.push(c);
      byDepartment.set(key, list);
    }
    const collator = new Intl.Collator('it', { sensitivity: 'base' });
    const groups = [...byDepartment.entries()].map(([key, items]) => ({
      key,
      label: key === ORPHAN ? 'Senza reparto' : departmentNameById.get(key) ?? 'Senza reparto',
      isOrphan: key === ORPHAN,
      // `teamFilterCompetences` è già ordinato per nome: l'ordine è preservato.
      competences: items,
    }));
    groups.sort((a, b) => {
      if (a.isOrphan !== b.isOrphan) return a.isOrphan ? 1 : -1;
      return collator.compare(a.label, b.label);
    });
    return groups;
  }, [teamDepartment, teamFilterCompetences, departmentNameById]);

  // Se il reparto cambia e la competenza scelta non è più tra quelle del
  // reparto, si azzera il secondo filtro (torna a "Tutte le competenze").
  useEffect(() => {
    if (
      teamCompetence !== 'all' &&
      !teamFilterCompetences.some((c) => c.id === teamCompetence)
    ) {
      setTeamCompetence('all');
    }
  }, [teamFilterCompetences, teamCompetence]);

  // Con una competenza selezionata il filtro reparto NON filtra le persone
  // (serve solo a restringere le competenze del secondo select): vogliamo poter
  // confrontare tutte le risorse su quella competenza, anche di altri reparti.
  // In compenso si escludono le persone senza alcun match su quella competenza
  // (match 0 o assente): l'elenco mostra solo chi la possiede almeno un po'.
  // Senza competenza selezionata, il reparto filtra le persone come di consueto.
  const filteredTeamRows = useMemo(() => {
    if (teamCompetence === 'all') {
      return filterTeam(teamRows, search, teamDepartment);
    }
    const withProficiency = teamRows.filter(
      (row) => row.topProficiencyPercent !== null && row.topProficiencyPercent > 0,
    );
    return filterTeam(withProficiency, search, 'all');
  }, [teamRows, search, teamDepartment, teamCompetence]);

  // Titolo della pagina coerente con la vista attiva (nessun "Livelli"
  // generico): Team / Competenze / Skill / Certificazioni.
  const title =
    view === 'team'
      ? 'Team'
      : view === 'skills'
        ? 'Skill'
        : view === 'certifications'
          ? 'Certificazioni'
          : 'Competenze';

  const subtitle =
    view === 'team'
      ? 'Per ogni persona: la competenza con il match più alto e il relativo match.'
      : view === 'skills'
        ? 'Per ogni skill: persone con almeno un voto e il voto medio pesato.'
        : view === 'certifications'
          ? 'Per ogni certificazione: quante persone la possiedono e le skill collegate.'
          : data === undefined
            ? 'Copertura delle competenze in azienda.'
            : `Per ogni competenza: persone con punteggio ≥ ${data.qualifiedRankThreshold}/100 e proficiency media.`;

  // Le viste "Team" e "Skill" gestiscono da sé loading/errore (tramite le
  // props della DataTable). Solo la vista "Competenze" usa il guard a pagina
  // intera sui dati aggregati per competenza.
  if (view === 'competences' && isLoading) {
    return (
      <section aria-labelledby="levels-title">
        <PageHeader
          title="Competenze"
          titleId="levels-title"
          subtitle="Copertura delle competenze in azienda."
        />
        <Loading>Caricamento dei livelli…</Loading>
      </section>
    );
  }

  if (view === 'competences' && (isError || !data)) {
    const message =
      error instanceof Error ? error.message : 'Impossibile caricare i livelli.';
    return (
      <section aria-labelledby="levels-title">
        <PageHeader
          title="Competenze"
          titleId="levels-title"
          subtitle="Copertura delle competenze in azienda."
        />
        <Alert tone="error">{message}</Alert>
        <div className="ui-toolbar" style={{ marginTop: 'var(--space-3)' }}>
          <Button type="button" onClick={() => void refetch()}>
            Riprova
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="catalog-levels" aria-labelledby="levels-title">
      {/* La scelta della vista (Team / Competenze / Skill / Certificazioni) è
          ora gestita dalla navigazione superiore (sezione Insights nella
          navbar), che imposta la query string `?view=`. Qui non c'è più il
          tablist interno: la pagina si limita a leggere `view` dall'URL. */}
      <PageHeader
        title={title}
        titleId="levels-title"
        subtitle={subtitle}
        actions={
          view === 'certifications' ? (
            <CertificationKindFilter
              value={certificationKind}
              onChange={setCertificationKind}
            />
          ) : undefined
        }
      />

      {view === 'team' ? (
        <>
          <Field
            id="team-search"
            label="Cerca persona"
            hint="Filtra per reparto e cerca per nome, email o competenza. Selezionando una competenza vedi il match di ogni persona su di essa."
          >
            <div className="competence-search">
              <span className="competence-search__department user-admin__select">
                <select
                  id="team-search-bu"
                  aria-label="Filtra per reparto"
                  value={teamDepartment}
                  onChange={(e) => setTeamDepartment(e.target.value)}
                >
                  <option value="all">Tutti i reparti</option>
                  {teamDepartmentOptions.hasNone && (
                    <option value="none">Senza reparto</option>
                  )}
                  {teamDepartmentOptions.names.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </span>
              <span className="competence-search__department user-admin__select">
                <select
                  id="team-search-competenza"
                  aria-label="Filtra per competenza"
                  value={teamCompetence}
                  disabled={competencesQuery.isLoading}
                  onChange={(e) => setTeamCompetence(e.target.value)}
                >
                  <option value="all">Tutte le competenze</option>
                  {teamCompetenceGroups !== null
                    ? teamCompetenceGroups.map((group) => (
                        <optgroup key={group.key} label={group.label}>
                          {group.competences.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </optgroup>
                      ))
                    : teamFilterCompetences.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                </select>
              </span>
              <TextInput
                id="team-search"
                className="competence-search__input"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca una persona…"
                autoComplete="off"
              />
            </div>
          </Field>

          <DataTable
            // Il remount al cambio di modalità (competenza selezionata o meno)
            // riapplica il `defaultSort` corretto e le colonne aggiornate.
            key={teamCompetence === 'all' ? 'team-top' : 'team-competence'}
            rows={filteredTeamRows}
            columns={teamColumns}
            rowKey={(row) => row.userId}
            loading={teamLevelsQuery.isLoading}
            error={
              teamLevelsQuery.isError
                ? teamLevelsQuery.error instanceof Error
                  ? teamLevelsQuery.error.message
                  : 'Impossibile caricare il team.'
                : null
            }
            emptyMessage="Nessuna persona corrisponde alla ricerca."
            // Con una competenza selezionata l'ordinamento di default è per
            // match decrescente; altrimenti alfabetico per persona.
            defaultSort={
              teamCompetence === 'all'
                ? { key: 'person', direction: 'asc' }
                : { key: 'topMatch', direction: 'desc' }
            }
            pageSize={15}
          />
        </>
      ) : view === 'competences' ? (
        <>
          <Field
            id="coverage-search"
            label="Cerca competenza"
            hint="Filtra per business unit e cerca per nome. La ricerca opera nella selezione corrente."
          >
            <div className="competence-search">
              <span className="competence-search__department user-admin__select">
                <select
                  id="coverage-search-bu"
                  aria-label="Filtra per business unit"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                >
                  <option value="all">Tutte le business unit</option>
                  {departmentOptions.hasNone && (
                    <option value="none">Senza reparto</option>
                  )}
                  {departmentOptions.names.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </span>
              <TextInput
                id="coverage-search"
                className="competence-search__input"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca una competenza…"
                autoComplete="off"
              />
            </div>
          </Field>

          <DataTable
            rows={filteredRows}
            columns={columns}
            rowKey={(row) => row.competenceId}
            emptyMessage="Nessuna competenza corrisponde alla business unit o alla ricerca."
            defaultSort={{ key: 'qualified', direction: 'desc' }}
            pageSize={15}
          />
        </>
      ) : view === 'skills' ? (
        <>
          <Field
            id="skill-coverage-search"
            label="Cerca skill"
            hint="Filtra per ambito e tipologia; la ricerca opera nella selezione corrente."
          >
            <div className="competence-search">
              {/* Filtro di ambito: tutte + una voce per business unit e per
                  professione. */}
              <span className="competence-search__department user-admin__select">
                <select
                  id="skill-coverage-scope"
                  aria-label="Filtra le skill per ambito"
                  value={skillScope}
                  disabled={competencesQuery.isLoading}
                  onChange={(e) => setSkillScope(e.target.value)}
                >
                  <option value="all">Tutte le skill</option>
                  {skillDepartmentOptions.length > 0 && (
                    <optgroup label="Per business unit">
                      {skillDepartmentOptions.map((d) => (
                        <option key={d.id} value={`${SKILL_DEPT_PREFIX}${d.id}`}>
                          {d.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {skillJobOptions.length > 0 && (
                    <optgroup label="Per professione">
                      {skillJobOptions.map((j) => (
                        <option key={j.id} value={`${SKILL_JOB_PREFIX}${j.id}`}>
                          {j.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </span>

              {/* Filtro per tipologia di skill (e-CF). */}
              <span className="competence-search__department user-admin__select">
                <select
                  id="skill-coverage-type"
                  aria-label="Filtra le skill per tipologia"
                  value={skillType}
                  disabled={skillsQuery.isLoading}
                  onChange={(e) => setSkillType(e.target.value as SkillTypeFilter)}
                >
                  <option value="all">Tutte le tipologie</option>
                  {SKILL_TYPE_VALUES.map((t) => (
                    <option key={t} value={t}>
                      {SKILL_TYPE_LABELS[t]}
                    </option>
                  ))}
                  <option value="none">Senza tipo</option>
                </select>
              </span>

              <TextInput
                id="skill-coverage-search"
                className="competence-search__input"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca una skill…"
                autoComplete="off"
              />
            </div>
          </Field>

          <SkillLevelMatrix
            rows={filteredSkillRows}
            descriptionBySkillId={descriptionBySkillId}
            certCountBySkillId={certCountBySkillId}
            loading={skillMatrixQuery.isLoading}
            error={
              skillMatrixQuery.isError
                ? skillMatrixQuery.error instanceof Error
                  ? skillMatrixQuery.error.message
                  : 'Impossibile caricare la matrice delle skill.'
                : null
            }
          />
        </>
      ) : (
        <CertificationLevelsView
          enabled={view === 'certifications'}
          kindFilter={certificationKind}
        />
      )}
    </section>
  );
}
